import React from 'react';
import { useNavigate } from 'react-router-dom';
import { m } from 'framer-motion';
import {
  FiMapPin, FiCalendar, FiClock, FiUser, FiMail, FiPhone,
  FiEdit3, FiPlus, FiXCircle, FiPackage, FiTag, FiTruck, FiCreditCard
} from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useLanguage } from '../../context/LanguageContext';
import { useMotionConfig } from '../../hooks/useReducedMotion';
import { useTranslation } from 'react-i18next';
import ManageHeader from './ManageHeader';
import BookingStatusBadge from './BookingStatusBadge';
import { groupBookingItems } from '../../utils/groupBookingItems';

export default function BookingOverview({ token, bookingData, refreshBooking }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { formatCurrency } = useCurrency();
  const { getProductTranslation, getLocationTranslation } = useLanguage();
  const { t } = useTranslation();
  const motion = useMotionConfig();

  const { booking, items, addons, merchant, location } = bookingData;
  const groupedItems = groupBookingItems(items);
  const groupedAddons = groupBookingItems(addons);

  const locationName = getLocationTranslation(
    location?.location_name,
    location?.location_translations,
    'location_name'
  );

  const canEdit = ['open', 'active'].includes(booking.booking_state) && booking.payment_state !== 'refunded';
  // Extend flow uses extension-specific pricing rules; pre-start changes go
  // through Edit (normal rules) to avoid two different prices for the same
  // conceptual change.
  const canExtend = ['open', 'active', 'returned'].includes(booking.booking_state)
    && booking.payment_state === 'paid'
    && new Date() >= new Date(booking.start_date);
  const canCancel = ['open', 'active'].includes(booking.booking_state) && new Date() < new Date(booking.start_date);
  // Total paid including manually recorded (e.g. cash) payments, computed
  // server-side from the payment ledger. Falls back to the raw amount_paid.
  const amountPaid = booking.amount_paid_total ?? booking.amount_paid ?? 0;
  // Gate on the actual outstanding balance rather than payment_state: a booking
  // can be marked 'paid' by the Stripe webhook after only a partial payment, so
  // a payment_state !== 'paid' check would wrongly hide the pay button.
  const hasOutstandingBalance = booking.total_amount > 0
    && amountPaid < booking.total_amount
    && ['open', 'active'].includes(booking.booking_state);

  const merchantTz = merchant?.timezone || 'UTC';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
      timeZone: merchantTz
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', timeZone: merchantTz });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ManageHeader merchant={merchant} token={token} />

      <main id="main-content" className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Order Header */}
        <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm text-gray-500 mb-1">{t('manage.orderNumber', 'Order')}</p>
              <h1 className="text-2xl font-bold text-gray-900">{booking.order_id}</h1>
            </div>
            <BookingStatusBadge bookingState={booking.booking_state} paymentState={booking.payment_state} />
          </div>
        </m.div>

        {/* Date & Time */}
        <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiCalendar} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
            {t('manage.rentalPeriod', 'Rental Period')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t('manage.pickUp', 'Pick-up')}</p>
              <p className="font-semibold text-gray-900">{formatDate(booking.start_date)}</p>
              <p className="text-sm text-gray-600">{formatTime(booking.start_date)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t('manage.returnBy', 'Return by')}</p>
              <p className="font-semibold text-gray-900">{formatDate(booking.end_date)}</p>
              <p className="text-sm text-gray-600">{formatTime(booking.end_date)}</p>
            </div>
          </div>
        </m.div>

        {/* Location */}
        <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <SafeIcon icon={FiMapPin} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
            {t('manage.location', 'Location')}
          </h2>
          <p className="font-medium text-gray-900">{locationName}</p>
          {location?.pickup_address && (
            <p className="text-sm text-gray-500 mt-1">{location.pickup_address.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()}</p>
          )}
        </m.div>

        {/* Delivery info if applicable */}
        {booking.delivery_street && (
          <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <SafeIcon icon={FiTruck} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
              {t('manage.deliveryAddress', 'Delivery Address')}
            </h2>
            <p className="text-gray-900">{booking.delivery_street}</p>
            <p className="text-gray-600 text-sm">
              {[booking.delivery_zip, booking.delivery_city, booking.delivery_country].filter(Boolean).join(', ')}
            </p>
            {booking.delivery_notes && (
              <p className="text-gray-500 text-sm mt-2">{booking.delivery_notes}</p>
            )}
          </m.div>
        )}

        {/* Items */}
        <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiPackage} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
            {t('manage.items', 'Items')}
          </h2>
          <div className="space-y-3">
            {groupedItems.map((group) => {
              const pt = group.product_types;
              const productName = getProductTranslation(
                pt?.product_name,
                pt?.product_type_translations,
                'product_name'
              );

              return (
                <div key={group.groupKey} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                  {pt?.picture_url && (
                    <img
                      src={pt.picture_url}
                      alt={productName}
                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{productName}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {group.size && (
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">
                          {group.size}
                        </span>
                      )}
                      {group.quantity > 1 && (
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">
                          {group.quantity}x
                        </span>
                      )}
                      {group.duration_label && (
                        <span className="text-xs text-gray-500">{group.duration_label}</span>
                      )}
                    </div>
                  </div>
                  <p className="font-semibold text-gray-900 flex-shrink-0">
                    {formatCurrency(group.total_price)}
                  </p>
                </div>
              );
            })}
          </div>
        </m.div>

        {/* Addons */}
        {groupedAddons.length > 0 && (
          <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <SafeIcon icon={FiPlus} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
              {t('manage.addons', 'Add-ons')}
            </h2>
            <div className="space-y-2">
              {groupedAddons.map((addon) => {
                const addonName = getProductTranslation(
                  addon.item_name || addon.product_types?.product_name,
                  addon.product_types?.product_type_translations,
                  'product_name'
                );
                return (
                <div key={addon.groupKey} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">{addonName}</p>
                    {addon.quantity > 1 && (
                      <span className="inline-block mt-1 text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">
                        {addon.quantity}x
                      </span>
                    )}
                  </div>
                  <p className="font-semibold text-gray-900">{formatCurrency(addon.total_price)}</p>
                </div>
                );
              })}
            </div>
          </m.div>
        )}

        {/* Pricing Summary */}
        <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiTag} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
            {t('manage.pricingSummary', 'Price Summary')}
          </h2>
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>{t('manage.subtotal', 'Subtotal')}</span>
              <span>{formatCurrency(booking.subtotal)}</span>
            </div>

            {booking.total_discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>{t('manage.discount', 'Discount')}{booking.discount_code ? ` (${booking.discount_code})` : ''}</span>
                <span>-{formatCurrency(booking.total_discount)}</span>
              </div>
            )}

            {booking.delivery_price > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>{t('manage.deliveryFee', 'Delivery fee')}</span>
                <span>{formatCurrency(booking.delivery_price)}</span>
              </div>
            )}

            {booking.total_tax > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>{t('manage.tax', 'Tax')}{booking.tax_included ? ` (${t('manage.included', 'incl.')})` : ''}</span>
                <span>{formatCurrency(booking.total_tax)}</span>
              </div>
            )}

            <div className="border-t border-gray-200 pt-3 mt-3">
              <div className="flex justify-between text-lg font-bold text-gray-900">
                <span>{t('manage.total', 'Total')}</span>
                <span>{formatCurrency(booking.total_amount)}</span>
              </div>
              {amountPaid > 0 && amountPaid < booking.total_amount && (
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>{t('manage.paid', 'Paid')}</span>
                  <span>{formatCurrency(amountPaid)}</span>
                </div>
              )}
            </div>
          </div>
        </m.div>

        {/* Customer Info */}
        <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiUser} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
            {t('manage.customerInfo', 'Your Information')}
          </h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-400" />
              <span className="text-gray-900">{booking.customer_first_name} {booking.customer_last_name}</span>
            </div>
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-400" />
              <span className="text-gray-900">{booking.customer_email}</span>
            </div>
            {booking.customer_phone && (
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiPhone} className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900">{booking.customer_phone}</span>
              </div>
            )}
          </div>
        </m.div>

        {/* Action Buttons */}
        {(canEdit || canExtend || canCancel || hasOutstandingBalance) && (
          <m.div {...motion.fadeInUp} className="space-y-3 pb-8">
            {hasOutstandingBalance && (
              <button
                onClick={() => navigate(`/b/${token}/pay`)}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-white transition-colors"
                style={{ backgroundColor: theme.colors.primary.base }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primary.hover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primary.base}
              >
                <SafeIcon icon={FiCreditCard} className="w-5 h-5" />
                {t('manage.payNow', 'Pay Now')} — {formatCurrency(booking.total_amount - amountPaid)}
              </button>
            )}

            {canEdit && (
              <button
                onClick={() => navigate(`/b/${token}/edit`)}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-white transition-colors"
                style={{ backgroundColor: theme.colors.primary.base }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primary.hover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primary.base}
              >
                <SafeIcon icon={FiEdit3} className="w-5 h-5" />
                {t('manage.editBooking', 'Edit Booking')}
              </button>
            )}

            {canExtend && (
              <button
                onClick={() => navigate(`/b/${token}/extend`)}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold border-2 transition-colors"
                style={{
                  borderColor: theme.colors.primary.base,
                  color: theme.colors.primary.base
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${theme.colors.primary.base}08`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <SafeIcon icon={FiClock} className="w-5 h-5" />
                {t('manage.extendRental', 'Extend Rental')}
              </button>
            )}

            {canCancel && (
              <button
                onClick={() => navigate(`/b/${token}/cancel`)}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-red-600 border-2 border-red-200 hover:bg-red-50 transition-colors"
              >
                <SafeIcon icon={FiXCircle} className="w-5 h-5" />
                {t('manage.cancelBooking', 'Cancel Booking')}
              </button>
            )}
          </m.div>
        )}
      </main>
    </div>
  );
}
